import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Card,
} from '@mui/material';

function AdminNotifications() {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`https://server-l9mf8npez-has-projects-fa0c5fad.vercel.app/api/get-notifications?password=${encodeURIComponent(password)}`);
      
      if (response.status === 401) {
        setError('Invalid password');
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const data = await response.json();
      setNotifications(data.notifications || []);
      setIsAuthenticated(true);
      setPassword('');
    } catch (err) {
      setError(err.message || 'Error loading notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setNotifications([]);
    setPassword('');
    setError('');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#faf9f7', py: 8 }}>
        {/* Header */}
        <Box sx={{ background: 'linear-gradient(135deg, #4AA9A9 0%, #5FC0C1 100%)', color: '#fff', py: 2, px: 0, borderRadius: '0 0 24px 24px', mb: 4 }}>
          <Container maxWidth="lg">
            <Typography variant="h5" fontWeight={900} letterSpacing={2}>
              Khayalethu Admin
            </Typography>
          </Container>
        </Box>

        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#5FC0C1', textAlign: 'center' }}>
              📋 Admin Panel
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: '#666', textAlign: 'center' }}>
              Enter your admin password to view notification subscribers
            </Typography>

            <Box component="form" onSubmit={handleLogin}>
              <TextField
                fullWidth
                type="password"
                label="Admin Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                disabled={loading}
                placeholder="Enter admin password"
              />

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !password}
                sx={{
                  mt: 3,
                  fontWeight: 700,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #4AA9A9 0%, #5FC0C1 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4AA9A9 0%, #1B3B4D 100%)'
                  }
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#faf9f7', py: 4 }}>
      {/* Header */}
      <Box sx={{ background: 'linear-gradient(135deg, #4AA9A9 0%, #5FC0C1 100%)', color: '#fff', py: 2, px: 0, borderRadius: '0 0 24px 24px', mb: 4 }}>
        <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" fontWeight={900} letterSpacing={2}>
            Khayalethu Admin
          </Typography>
          <Button
            variant="outlined"
            sx={{
              color: '#fff',
              borderColor: '#fff',
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' }
            }}
            onClick={handleLogout}
          >
            Logout
          </Button>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Card elevation={3} sx={{ p: 4, borderRadius: 3 }}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#5FC0C1', mb: 1 }}>
                📧 Notification Subscribers
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Total subscribers: <strong>{notifications.length}</strong>
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              sx={{
                background: 'linear-gradient(135deg, #4AA9A9 0%, #5FC0C1 100%)',
                fontWeight: 700
              }}
            >
              🔄 Refresh
            </Button>
          </Box>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {notifications.length === 0 ? (
            <Alert severity="info">
              No subscribers yet. They will appear here as people subscribe to notifications.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#5FC0C1' }}>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Email</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Project</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Subscribe Date</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notifications.map((notification, index) => (
                    <TableRow key={notification.id} sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        {notification.email}
                      </TableCell>
                      <TableCell>
                        <Box sx={{
                          display: 'inline-block',
                          background: '#fef7ed',
                          color: '#5FC0C1',
                          px: 2,
                          py: 0.5,
                          borderRadius: 2,
                          fontWeight: 600,
                          fontSize: '12px'
                        }}>
                          {notification.project}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '12px' }}>
                        {formatDate(notification.created_at)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{
                          display: 'inline-block',
                          background: notification.notified ? '#d1fae5' : '#fecaca',
                          color: notification.notified ? '#065f46' : '#991b1b',
                          px: 2,
                          py: 0.5,
                          borderRadius: 2,
                          fontWeight: 600,
                          fontSize: '12px'
                        }}>
                          {notification.notified ? '✅ Notified' : '⏳ Pending'}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        <Box sx={{ mt: 4, p: 3, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e5e7eb' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1f2937' }}>
            💡 Tips
          </Typography>
          <ul style={{ color: '#666', lineHeight: 1.8 }}>
            <li>Refresh the page to get the latest subscriber list</li>
            <li>Each email is unique - duplicate subscriptions are prevented</li>
            <li>Use this data to send bulk notifications when Riverview Flat is ready</li>
          </ul>
        </Box>
      </Container>
    </Box>
  );
}

export default AdminNotifications;

